import { useState, useEffect, useCallback, useRef } from 'react';
import { usePresencia } from 'context/PresenciaContext';
import { Link } from 'react-router-dom';
import { voteService, attendanceService } from 'services/api';
import { ComunicadosList, useComunicados } from 'components/ComunicadosPanel';
import GlosarioModal from './GlosarioModal';
import { useMeeting } from 'context/MeetingContext';
import { useAuth } from 'context/AuthContext';
import RepresentacionesModal from './RepresentacionesModal';
import OrdenDiaModal from './OrdenDiaModal';
import PostulacionModal from './PostulacionModal';
import AnexosModal from './AnexosModal';
import ComunicadosModal from './ComunicadosModal';
import DatosAsambleaModal from './DatosAsambleaModal';
import ModalidadModal from './ModalidadModal';
import VoteHistoryModal from './VoteHistoryModal';
import VoteModal from './VoteModal';
import VotoValePor from './VotoValePor';
import DelegatedScreen from './DelegatedScreen';
import styles from './UserDashboard.module.css';
import { etiquetaTipo } from 'utils/etiquetas';
import useCierreAlerta from 'utils/useCierreAlerta';

// Frecuencias de consulta del panel del propietario. Medido 2026-09-28 en la
// réplica del droplet ("sala llena"): cada propietario conectado pedía ~0,7
// consultas/s y con 500 conectados el backend colapsaba. Resultados cada 5 s
// (la respuesta viene de la caché compartida del servidor), "mis votos" solo
// cuando puede haber cambiado (+ respaldo cada 30 s), latido cada 60 s en
// PresenciaContext (F-26: el servidor marca "retirado" tras 5 min sin latido;
// en presencial no vence) y TODO en pausa con la
// pestaña oculta — al volver se refresca de inmediato.
const POLL_MS       = 5000;
const POLL_ASM_MS   = 20000; // buscar la asamblea en curso (antes 15 s; medido 2026-10-03: ~39 % de la carga del panel)
const MIS_VOTOS_MS  = 30000;
const ESTADO_MS     = 60000; // my-status: poderes / "Tu voto vale por N" (~10 consultas a la BD)

// true mientras la pestaña está a la vista.
function usePestanaVisible() {
  const [visible, setVisible] = useState(document.visibilityState === 'visible');
  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);
  return visible;
}

const VOTE_LABEL = { favor: 'A favor', contra: 'En contra', abstencion: 'Abstención' };
const VOTE_ICON  = { favor: 'fa-thumbs-up', contra: 'fa-thumbs-down', abstencion: 'fa-minus' };
const VOTE_COLOR = { favor: '#12b76a', contra: '#f04438', abstencion: '#8a92a6' };

// ── Pantalla de bloqueo — propiedad sin membresía activa ─────────────────────
// P1: aviso (no bloquea) cuando el conjunto de la asamblea en curso está en
// período de gracia; antes este estado impedía participar y votar.
const AvisoGracia = ({ compacto = false }) => (
  <div className={compacto ? `${styles.avisoGracia} ${styles.avisoGraciaCompacto}` : styles.avisoGracia}>
    <i className={`fas fa-circle-info ${styles.icono8}`} />
    La membresía de este conjunto venció. Esta asamblea ya estaba en curso y se puede terminar con normalidad.
  </div>
);

function PropertyBlockedScreen() {
  const { logout } = useAuth();
  return (
    <div className={styles.bloqueoPagina}>
      <div className={styles.bloqueoTarjeta}>
        <div className={styles.bloqueoCirculo}>
          <i className={`fas fa-ban ${styles.bloqueoIcono}`} />
        </div>
        <h2 className={styles.bloqueoTitulo}>
          Acceso no disponible
        </h2>
        <p className={styles.bloqueoTexto}>
          La membresía del conjunto al que perteneces ha vencido y el período de
          gracia ha expirado. El acceso a las asambleas no está disponible en este momento.
        </p>
        <div className={styles.bloqueoAviso}>
          <i className={`fas fa-info-circle ${styles.icono8}`} />
          Para recuperar el acceso, comunícate con el administrador de tu
          conjunto para que renueve la membresía del sistema.
        </div>
        <button
          className={`btn btn-secondary ${styles.botonAncho}`}
          onClick={logout}
        >
          <i className={`fas fa-right-from-bracket ${styles.icono8}`} />
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}


// ── Tarjeta de acción reutilizable ────────────────────────────────────────────
function InfoCard({ icon, color, title, desc, onClick }) {
  return (
    <div className={`card ${styles.infoTarjeta}`} style={{ '--tono': color, '--fondo': color + '18' }}
      onClick={onClick}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.10)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = ''}>
      <div className={styles.infoFila}>
        <div className={styles.infoIcono}>
          <i className={`fas ${icon} ${styles.infoGlifo}`} />
        </div>
        <div className={styles.infoTexto}>
          <div className={styles.infoTitulo}>{title}</div>
          <div className={styles.infoDesc}>{desc}</div>
        </div>
        <i className={`fas fa-chevron-right ${styles.infoFlecha}`} />
      </div>
    </div>
  );
}

// ── Aviso: falta el teléfono de contacto ─────────────────────────────────────
// Se muestra antes de la asamblea (sin asamblea / pantalla de registro) cuando
// el propietario no tiene teléfono. El correo no se pide corregir: es la llave
// de acceso y ya quedó validado al activar la cuenta / iniciar sesión.
function ContactBanner({ phone }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('contactBannerDismissed') === '1'; } catch { return false; }
  });
  if (phone || dismissed) return null;
  const close = () => {
    setDismissed(true);
    try { sessionStorage.setItem('contactBannerDismissed', '1'); } catch { /* noop */ }
  };
  return (
    <div className={styles.contactoBanner}>
      <i className={`fas fa-phone ${styles.contactoIcono}`} />
      <div className={styles.contactoTexto}>
        <strong>Confirma tu teléfono de contacto.</strong> Lo usamos para avisarte de las
        asambleas de tu conjunto.{' '}
        <Link to="/admin/perfil" className={styles.contactoEnlace}>Ir a mi perfil →</Link>
      </div>
      <button onClick={close} aria-label="Cerrar" className={styles.contactoCerrar}>✕</button>
    </div>
  );
}

// ── Portal del propietario cuando no hay asamblea en curso ───────────────────
// Solo muestra comunicados SIN LEER — al leer uno desaparece de aquí y queda en
// "Mis unidades → Historial de comunicados".
function NoAssemblyScreen() {
  const { items, unread, reload } = useComunicados();
  const [showGlosario, setShowGlosario] = useState(false);
  return (
    <div className={styles.sinAsamblea}>
      <div className={styles.sinCabecera}>
        <div className={styles.sinCirculo}>
          <i className={`fas fa-hourglass-half ${styles.sinIcono}`} />
        </div>
        <h2 className={styles.sinTitulo}>Sin asamblea activa</h2>
        <p className={styles.sinTexto}>
          No hay ninguna asamblea en progreso para tu propiedad.<br />
          Esta pantalla se actualiza sola cuando una asamblea inicie.
        </p>
      </div>

      <div className={`card ${styles.sinComunicados}`}>
        <div className={styles.sinFila}>
          <i className={`fas fa-bullhorn ${styles.sinAltavoz}`} />
          <span className={styles.sinSubtitulo}>Comunicados sin leer</span>
          {unread > 0 && <span className={styles.sinContador}>{unread}</span>}
        </div>
        <ComunicadosList items={items} onlyUnread onChange={reload}
          emptyText="Estás al día. Los comunicados que ya leíste quedan en “Mis unidades”." />
      </div>

      <div className={styles.sinBotones}>
        <button className="btn btn-secondary" onClick={() => setShowGlosario(true)}>
          <i className="fas fa-book" /> Glosario
        </button>
        <Link to="/admin/perfil" className="btn btn-secondary">
          <i className="fas fa-user" /> Mi perfil
        </Link>
        <Link to="/admin/unidades" className="btn btn-secondary">
          <i className="fas fa-door-open" /> Mis unidades y comunicados
        </Link>
      </div>

      {showGlosario && <GlosarioModal onClose={() => setShowGlosario(false)} />}

      <div className={styles.sinPie}>
        <i className={`fas fa-sync ${styles.sinSyncIcono}`} />Verificando asamblea cada {POLL_ASM_MS / 1000}s
      </div>
    </div>
  );
}

// ── Pantalla: Registrar asistencia ────────────────────────────────────────────
// URLs de las páginas legales de la landing (Astro, Cloudflare Pages). Se abren
// en pestaña nueva. Base configurable con REACT_APP_LANDING_URL (en local, p. ej.
// http://localhost:4321); sin ella, producción. Con barra final: la landing
// redirige (308) la versión sin ella.
const LANDING_URL = (process.env.REACT_APP_LANDING_URL || 'https://eventifycol.com').replace(/\/+$/, '');
const TERMS_URL   = LANDING_URL + '/terminos/';
const PRIVACY_URL = LANDING_URL + '/tratamiento-datos/';

function ConfirmAttendanceScreen({ assembly, onConfirm, confirming, propertyReadOnly, consentGiven }) {
  const [terms,   setTerms]   = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const needsConsent = !consentGiven;
  const canConfirm   = !confirming && (!needsConsent || (terms && privacy));

  return (
    <div className={styles.confirmarPagina}>

      {/* Hero */}
      <div className={styles.confirmarHero}>
        <div className={styles.confirmarHeroCirculo} />
        <div className={styles.confirmarHeroContenido}>
          <div className={styles.confirmarHeroIcono}>
            <i className={`fas fa-gavel ${styles.confirmarHeroGlifo}`} />
          </div>
          <div className={styles.confirmarHeroChip}>
            <i className={`fas fa-circle ${styles.confirmarHeroPunto}`} />Asamblea en curso
          </div>
          <h2 className={styles.confirmarHeroTitulo}>
            {assembly.property?.name}
          </h2>
          <p className={styles.confirmarHeroSub}>
            Asamblea #{assembly.number_assembly}
            {assembly.assembly_type ? ` · ${etiquetaTipo(assembly.assembly_type)}` : ''}
          </p>
        </div>
      </div>

      {/* Card de bienvenida */}
      <div className={`card ${styles.confirmarTarjeta}`}>
        <div className={styles.confirmarBienvenidaIcono}>
          <i className={`fas fa-hand-wave ${styles.confirmarBienvenidaGlifo}`} />
        </div>

        <h3 className={styles.confirmarTitulo}>
          {consentGiven ? 'Vuelve a entrar a la asamblea' : '¡Bienvenido a la asamblea!'}
        </h3>
        <p className={needsConsent ? `${styles.confirmarTexto} ${styles.confirmarTextoConsent}` : styles.confirmarTexto}>
          {consentGiven
            ? 'Habías salido de la asamblea. Presiona el botón para volver a entrar y seguir participando en las votaciones.'
            : 'Para participar y votar en las preguntas de esta asamblea, primero debes registrar tu asistencia. Al presionar el botón confirmarás que estás presente en la asamblea.'}
        </p>

        {/* Consentimiento de tratamiento de datos — Ley 675 / Ley 1581 */}
        {propertyReadOnly && <AvisoGracia />}
        {needsConsent && (
          <div className={styles.consentimiento}>
            <label className={`${styles.consentimientoFila} ${styles.consentimientoFilaPrimera}`}>
              <input type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)}
                className={styles.casilla} />
              <span className={styles.consentimientoTexto}>
                Acepto los{' '}
                <a href={TERMS_URL} target="_blank" rel="noreferrer" className={styles.consentimientoEnlace}>
                  Términos y Condiciones
                </a>{' '}de la plataforma para participar en esta asamblea.
              </span>
            </label>
            <label className={styles.consentimientoFila}>
              <input type="checkbox" checked={privacy} onChange={e => setPrivacy(e.target.checked)}
                className={styles.casilla} />
              <span className={styles.consentimientoTexto}>
                Autorizo el tratamiento de mis datos personales (nombre, documento, unidad,
                coeficiente, correo y registro de participación) para la gestión de esta
                asamblea, conforme a la{' '}
                <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className={styles.consentimientoEnlace}>
                  Política de Tratamiento de Datos
                </a>{' '}(Ley 1581 de 2012).
              </span>
            </label>
          </div>
        )}

        <button
          className={`btn btn-primary ${styles.confirmarBoton} ${canConfirm ? '' : styles.confirmarBotonBloqueado}`}
          onClick={() => onConfirm({ accept_terms: true, accept_privacy: true })}
          disabled={!canConfirm}
        >
          {confirming
            ? <><div className={`spinner ${styles.confirmarSpinner}`} /> Registrando asistencia...</>
            : <><i className={`fas fa-circle-check ${styles.icono8}`} />Participar — Registrar mi asistencia</>
          }
        </button>
        {needsConsent && !canConfirm && !confirming && (
          <p className={styles.confirmarAyuda}>
            Marca ambas casillas para continuar.
          </p>
        )}

        <p className={styles.confirmarPie}>
          <i className={`fas fa-info-circle ${styles.icono6}`} />
          Tu asistencia quedará registrada con la hora actual
        </p>
      </div>
    </div>
  );
}

// ── Pantalla: Sala de votación ─────────────────────────────────────────────────
function VotingScreen({ assembly, data, myVotes, incompletas, activeQuestion, setActiveQuestion, vote, voting, alert, setAlert, user, propertyReadOnly, onJoinMeeting, onLeave, attendanceMode, onModeSaved, voto }) {
  // F-18: representante externo (cuenta temporal). F-24: también quien está en
  // esta asamblea SOLO como representante (propietario de otro conjunto): no ve
  // las tarjetas que son de los propietarios de este conjunto.
  const esExterno = !!user?.is_temporary_rep || assembly?.rol === 'representante';
  const [showHistory,          setShowHistory]          = useState(false);
  const [showRepresentaciones, setShowRepresentaciones] = useState(false);
  const [showOrdenDia,         setShowOrdenDia]         = useState(false);
  const [showPostulacion,      setShowPostulacion]      = useState(false);
  const [showAnexos,           setShowAnexos]           = useState(false);
  const [showComunicados,      setShowComunicados]      = useState(false);
  const [showDatos,            setShowDatos]            = useState(false);
  const [showModalidad,        setShowModalidad]        = useState(false);
  const [showGlosario,         setShowGlosario]         = useState(false);

  const votedQuestions   = data ? data.questions.filter(q => myVotes[q.id]).sort((a, b) => b.id - a.id) : [];
  const totalQuestions   = data?.questions?.length || 0;
  const totalVoted       = votedQuestions.length;
  const pctVoted         = totalQuestions ? Math.round((totalVoted / totalQuestions) * 100) : 0;

  return (
    <div className={styles.salaPagina}>

      {alert && (
        <div className={`alert alert-${alert.type} ${styles.salaAviso}`} onClick={() => setAlert(null)}>
          {alert.type === 'success'
            ? <><i className={`fas fa-check-circle ${styles.icono8}`} />{alert.message}</>
            : alert.type === 'info'
              ? <><i className={`fas fa-spinner fa-spin ${styles.icono8}`} />{alert.message}</>
              : <><i className={`fas fa-circle-exclamation ${styles.icono8}`} />{alert.message}</>}
        </div>
      )}

      {/* Hero */}
      <div className={styles.salaHero}>
        <div className={styles.salaHeroCirculoA} />
        <div className={styles.salaHeroCirculoB} />
        <div className={styles.salaHeroContenido}>
          <div className={styles.salaHeroFila}>
            <span className={styles.salaHeroChip}>
              <i className={`fas fa-circle ${styles.puntoChico}`} />Asamblea en curso
            </span>
          </div>
          <h2 className={styles.salaHeroTitulo}>{assembly.property?.name}</h2>
          <p className={styles.salaHeroSub}>
            Asamblea #{assembly.number_assembly}{assembly.assembly_type ? ` · ${etiquetaTipo(assembly.assembly_type)}` : ''}
          </p>
          {/* #10: por cuántas unidades cuenta su voto (solo si son 2 o más) */}
          <VotoValePor voto={voto} variante="sala" />
          {['virtual','hybrid'].includes(assembly.assembly_model) && assembly.zoom_meeting_number && (
            <div className={styles.salaZoom}>
              <button
                onClick={() => assembly.meeting_started && onJoinMeeting && onJoinMeeting()}
                disabled={!assembly.meeting_started}
                className={`${styles.salaZoomBoton} ${assembly.meeting_started ? styles.salaZoomBotonActivo : ''}`}>
                <i className={`fas fa-video ${assembly.meeting_started ? styles.salaZoomIconoActivo : styles.salaZoomIcono}`} />
                {assembly.meeting_started ? 'Unirse a la reunión virtual' : 'Reunión no iniciada aún'}
              </button>
              <span className={styles.salaZoomNota}>
                <i className={`fas ${assembly.meeting_started ? 'fa-circle-check' : 'fa-clock'} ${styles.salaZoomNotaIcono} ${assembly.meeting_started ? styles.salaZoomNotaIconoActivo : ''}`} />
                {assembly.meeting_started
                  ? 'La reunión está activa — puedes unirte ahora'
                  : 'Disponible cuando el administrador inicie la reunión'}
              </span>
            </div>
          )}
          <div className={styles.salaKpis}>
            {[
              {
                label: 'Quórum en vivo',
                value: data?.quorum_info ? `${data.quorum_info.pct_asistencia}%` : '—',
                sub:   data?.quorum_info
                  ? `de ${data.quorum_info.quorum_requerido}% requerido${data.quorum_info.quorum_alcanzado ? ' · alcanzado' : ''}`
                  : null,
                icon: 'fa-users',
              },
              { label: 'Preguntas',  value: totalQuestions,           icon: 'fa-circle-question' },
              { label: 'Mis votos',  value: `${totalVoted}/${totalQuestions}`, icon: 'fa-check-to-slot' },
            ].map(k => (
              <div key={k.label} className={styles.salaKpi}>
                <div className={styles.salaKpiEtiqueta}>
                  <i className={`fas ${k.icon} ${styles.icono5}`} />{k.label}
                </div>
                <div className={styles.salaKpiValor}>{k.value}</div>
                {k.sub && <div className={styles.salaKpiSub}>{k.sub}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tarjetas */}
      <div className={styles.salaTarjetas}>

        {/* Participación */}
        <div className={`card ${styles.salaCard}`}>
          <div className={styles.salaCardFila}>
            <div className={styles.salaCardIcono}>
              <i className={`fas fa-check-to-slot ${styles.colorAcento}`} />
            </div>
            <span className={styles.salaCardTitulo}>Mi participación</span>
          </div>
          {totalQuestions === 0 ? (
            <p className={styles.salaCardVacio}>Aún no hay preguntas activas</p>
          ) : (
            <>
              <div className={styles.salaBarra}>
                <div className={styles.salaBarraRelleno} style={{ '--ancho': `${pctVoted}%` }} />
              </div>
              <div className={styles.salaCardTexto}>
                Has votado en <strong className={styles.textoFuerte}>{totalVoted}</strong> de <strong className={styles.textoFuerte}>{totalQuestions}</strong> preguntas
              </div>
            </>
          )}
          {activeQuestion && !myVotes[activeQuestion.id] && (
            <>
              {propertyReadOnly && <div className={styles.salaAvisoGracia}><AvisoGracia compacto /></div>}
              <button className={`btn btn-primary ${styles.salaVotarBoton} ${propertyReadOnly ? '' : styles.salaVotarBotonSolo}`}
                onClick={() => setActiveQuestion(activeQuestion)}>
                <i className="fas fa-vote-yea" /> {incompletas[activeQuestion.id] ? 'Completar mi voto' : 'Votar ahora'}
              </button>
            </>
          )}
        </div>

        {/* Historial */}
        <div className={`card ${styles.salaCard} ${styles.salaCardClicable}`} onClick={() => setShowHistory(true)}>
          <div className={styles.salaCardFila}>
            <div className={`${styles.salaCardIcono} ${styles.salaCardIconoMorado}`}>
              <i className={`fas fa-history ${styles.colorMorado}`} />
            </div>
            <span className={styles.salaCardTitulo}>Historial de votos</span>
            {votedQuestions.length > 0 && (
              <span className={styles.salaBadge}>
                {votedQuestions.length}
              </span>
            )}
          </div>
          {votedQuestions.length === 0 ? (
            <p className={styles.salaCardVacio}>Aún no has votado</p>
          ) : (
            <div className={styles.salaHistLista}>
              {votedQuestions.slice(0, 3).map(q => {
                const answer = myVotes[q.id];
                const color  = VOTE_COLOR[answer] || 'var(--accent)';
                return (
                  <div key={q.id} className={styles.salaHistFila}>
                    <i className={`fas ${VOTE_ICON[answer] || 'fa-check'} ${styles.salaHistIcono}`} style={{ '--tono': color }} />
                    <span className={styles.salaHistPregunta}>{q.question_text}</span>
                    <span className={styles.salaHistRespuesta} style={{ '--tono': color }}>{VOTE_LABEL[answer] || answer}</span>
                  </div>
                );
              })}
              {votedQuestions.length > 3 && (
                <div className={styles.salaHistMas}>
                  +{votedQuestions.length - 3} más · Ver todo →
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tarjetas adicionales */}
      <div className={styles.salaTarjetas}>

        {/* Representaciones */}
        <InfoCard
          icon="fa-users" color="#0ea5e9"
          title="Representaciones"
          desc="Propietarios que te han delegado su voto"
          onClick={() => setShowRepresentaciones(true)}
        />

        {/* Orden del día */}
        <InfoCard
          icon="fa-list-check" color="#12b76a"
          title="Orden del día"
          desc="Puntos a tratar en la asamblea"
          onClick={() => setShowOrdenDia(true)}
        />

        {/* Postularse — solo propietarios (F-18: el externo no se postula) */}
        {!esExterno && <InfoCard
          icon="fa-hand-point-up" color="#f04438"
          title="Postularse"
          desc="Postúlate para un cargo o comité"
          onClick={() => setShowPostulacion(true)}
        />}

        {/* Anexos */}
        <InfoCard
          icon="fa-paperclip" color="#f79009"
          title="Anexos"
          desc="Documentos adjuntos de la asamblea"
          onClick={() => setShowAnexos(true)}
        />

        {/* Comunicados — avisos del conjunto, solo para sus propietarios */}
        {!esExterno && <InfoCard
          icon="fa-bullhorn" color="#f97316"
          title="Comunicados"
          desc="Avisos de la administración"
          onClick={() => setShowComunicados(true)}
        />}

        {/* Glosario */}
        <InfoCard
          icon="fa-book" color="#0ea5e9"
          title="Glosario"
          desc="Qué significan los términos de la asamblea"
          onClick={() => setShowGlosario(true)}
        />

        {/* Datos Asamblea */}
        <InfoCard
          icon="fa-circle-info" color="#6c63ff"
          title="Datos de la asamblea"
          desc="Información general del evento"
          onClick={() => setShowDatos(true)}
        />

        {/* Modalidad — de las filas propias del propietario; no aplica al externo */}
        {!esExterno && <InfoCard
          icon="fa-rotate" color="#8b5cf6"
          title="Modalidad de participación"
          desc={attendanceMode === 'virtual' ? 'Participas: Virtual — toca para cambiar'
              : attendanceMode === 'inplace' ? 'Participas: Presencial — toca para cambiar'
              : 'Indica cómo vas a participar'}
          onClick={() => setShowModalidad(true)}
        />}

      </div>

      <div className={styles.salaPie}>
        <button
          onClick={onLeave}
          className={styles.salaSalir}>
          <i className={`fas fa-right-from-bracket ${styles.colorPeligro}`} />
          Salir de la asamblea
        </button>
        <div className={styles.salaPieInfo}>
          <i className={`fas fa-sync ${styles.icono6}`} />
          Actualizando cada {POLL_MS / 1000}s · Conectado como <strong>{user?.name}</strong>
        </div>
      </div>

      {/* Modal votación */}
      {activeQuestion && !myVotes[activeQuestion.id] && (
        <VoteModal question={activeQuestion} incompleta={incompletas[activeQuestion.id]} onVote={vote} onClose={() => setActiveQuestion(null)} voting={voting} voto={voto} aviso={alert} />
      )}

      {/* Modales de tarjetas */}
      {showRepresentaciones && <RepresentacionesModal assembly={assembly} onClose={() => setShowRepresentaciones(false)} />}
      {showOrdenDia         && <OrdenDiaModal         assembly={assembly} onClose={() => setShowOrdenDia(false)} />}
      {showPostulacion      && <PostulacionModal       assembly={assembly} onClose={() => setShowPostulacion(false)} />}
      {showAnexos           && <AnexosModal            assembly={assembly} onClose={() => setShowAnexos(false)} />}
      {showComunicados      && <ComunicadosModal       onClose={() => setShowComunicados(false)} />}
      {showDatos            && <DatosAsambleaModal     assembly={assembly} onClose={() => setShowDatos(false)} />}
      {showModalidad        && <ModalidadModal         assembly={assembly} currentMode={attendanceMode} onSaved={onModeSaved} onClose={() => setShowModalidad(false)} />}
      {showGlosario         && <GlosarioModal          onClose={() => setShowGlosario(false)} />}

      {/* Modal historial */}
      {showHistory && (
        <VoteHistoryModal votedQuestions={votedQuestions} myVotes={myVotes} onClose={() => setShowHistory(false)} />
      )}
    </div>
  );
}

// ── Componente principal ───────────────────────────────────────────────────────
export default function UserDashboard() {
  const { user, refreshUser } = useAuth();

  // El user del login viene SIN teléfono, y AuthContext solo refresca /me al
  // montar la app (no tras un login "viejo" ni un hot-reload). Acá se re-
  // consulta /me al entrar al panel y NO se decide sobre el aviso "confirma
  // tu teléfono" hasta tener esa respuesta fresca — así nunca sale por un
  // objeto de sesión incompleto. El teléfono se toma directo del resultado.
  const [contactChecked, setContactChecked] = useState(false);
  const [freshPhone,     setFreshPhone]     = useState(undefined);
  useEffect(() => {
    let alive = true;
    Promise.resolve(refreshUser?.())
      .then(u => { if (alive && u) setFreshPhone(u.phone || ''); })
      .finally(() => { if (alive) setContactChecked(true); });
    return () => { alive = false; };
  }, [refreshUser]);
  const contactPhone = freshPhone !== undefined ? freshPhone : user?.phone;

  // Estado general
  const { abrirReunion } = useMeeting();
  // Propietario/representante: cerrar la ventana solo lo saca a él (sin alCerrar).
  const unirseReunion = () => assembly?.zoom_meeting_number && abrirReunion({
    assemblyId:   assembly.id,
    assemblyName: `${assembly.property?.name || 'Asamblea'} #${assembly.number_assembly}`,
    isModerator:  false,
  });
  const [assembly,        setAssembly]        = useState(null);
  const [assemblyLoading, setAssemblyLoading] = useState(true);
  const [confirmed,       setConfirmed]       = useState(false);   // asistencia confirmada
  const [confirming,      setConfirming]      = useState(false);
  const [consentGiven,    setConsentGiven]    = useState(false);   // consentimiento tratamiento de datos ya otorgado
  const [attendanceMode,  setAttendanceMode]  = useState(null);    // 'virtual' | 'inplace' | null
  const [delegacion,      setDelegacion]      = useState({ all: false, delegated: [] }); // poderes que ESTE propietario dio
  const [voto,            setVoto]            = useState(null); // #10: por cuántas unidades cuenta su voto
  // #9: id de la asamblea cuyo my-status ya llegó. Hasta entonces se muestra
  // "cargando" en vez de pintar un instante la pantalla de "Participar". Se
  // guarda el id (no un booleano) para que el refresco de la asamblea cada 15 s
  // no vuelva a mostrar el spinner.
  const [estadoDe,        setEstadoDe]        = useState(null);
  const [propertyReadOnly, setPropertyReadOnly] = useState(false); // propiedad en período de gracia
  const [propertyBlocked,  setPropertyBlocked]  = useState(false); // propiedad bloqueada (+14 días)

  // Estado votación
  const [data,           setData]           = useState(null);
  const [myVotes,        setMyVotes]        = useState({});
  const [incompletas,    setIncompletas]    = useState({});
  const pestanaVisible = usePestanaVisible();
  const [voting,         setVoting]         = useState(null);
  const [alert,          setAlert]          = useState(null);
  const cerrarAlertaEn = useCierreAlerta(setAlert);
  const [activeQuestion, setActiveQuestion] = useState(null);

  const asmIntervalRef  = useRef(null);
  const voteIntervalRef = useRef(null);

  // ── Buscar asamblea activa ───────────────────────────────────────────────
  // F-24: con más de una asamblea en curso (dueño en dos conjuntos, o con un
  // poder en otro), cada DISPOSITIVO elige cuál abrir y lo recuerda: para estar
  // presente en las dos se usa un dispositivo por asamblea (el latido va solo
  // a la asamblea abierta en esa pantalla).
  const [asambleas, setAsambleas] = useState([]);
  const leerElegida = () => { try { return localStorage.getItem('eventify_asamblea_elegida') || ''; } catch { return ''; } };
  const checkAssembly = useCallback(async () => {
    try {
      const elegida = leerElegida();
      const { data: res } = await voteService.activeAssembly(elegida ? { assembly_id: elegida } : undefined);
      setAsambleas(res.asambleas || []);
      setAssembly(res.assembly || null);
      setPropertyReadOnly(!!res.propertyReadOnly);
      setPropertyBlocked(!!res.propertyBlocked);
      setAssemblyLoading(false);
    } catch {
      setAssemblyLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!pestanaVisible) return;
    checkAssembly();
    asmIntervalRef.current = setInterval(checkAssembly, POLL_ASM_MS);
    return () => clearInterval(asmIntervalRef.current);
  }, [checkAssembly, pestanaVisible]);

  // ── Verificar asistencia cuando hay asamblea ─────────────────────────────
  // Al entrar a la asamblea y luego cada ESTADO_MS con la pestaña visible (así
  // un poder recibido o entregado a mitad de la asamblea se refleja en menos de
  // un minuto). Antes dependía del objeto `assembly`, que checkAssembly renueva
  // cada 15 s: my-status (~10 consultas a la BD) salía cada 15 s por persona —
  // medido 2026-09-29, eso solo saturaba el backend con 2.000 conectados.
  const presencia = usePresencia();
  const salioAManoRef = useRef(false);   // pulsó «Salir»: el repaso del reingreso automático no debe devolverla
  const cargarEstado = useCallback((id) =>
    attendanceService.myStatus(id)
      .then(async r => {
        // Recargar la página (F5) la da por retirada (pagehide → leave). Si la marca es
        // reciente y ya había dado su consentimiento, reingresa sola (confirmar no pide el
        // consentimiento de nuevo). Si falla (finalizada, membresía vencida…), queda la
        // pantalla normal de «Participar».
        let confirmado = r.data.confirmed;
        const recargo = presencia.consumirMarcaRecarga(id);
        if (!confirmado && r.data.consent_given && recargo) {
          try {
            await attendanceService.confirm(id, {});
            confirmado = true;
            // El «leave» del F5 puede llegar al servidor DESPUÉS de este reingreso y dejarla retirada
            // (el latido no limpia exit_time): se revisa a los 3 s y se reingresa una vez más si hace falta.
            setTimeout(() => {
              attendanceService.myStatus(id)
                .then(x => { if (!salioAManoRef.current && !x.data.confirmed && x.data.consent_given) return attendanceService.confirm(id, {}).then(() => setConfirmed(true)); })
                .catch(() => {});
            }, 3000);
          } catch { /* pantalla normal */ }
        }
        setConfirmed(confirmado);
        setConsentGiven(!!r.data.consent_given);
        setAttendanceMode(r.data.attendance_mode || null);
        setDelegacion({ all: !!r.data.all_delegated, delegated: r.data.delegated || [] });
        setVoto(r.data.voto || null);
      })
      .catch(() => {})
      .finally(() => setEstadoDe(id)), [presencia]);
  const asmIdEstado = assembly?.id;
  useEffect(() => {
    if (!asmIdEstado) { setConfirmed(false); setConsentGiven(false); setAttendanceMode(null); setDelegacion({ all: false, delegated: [] }); setVoto(null); return; }
    cargarEstado(asmIdEstado);
  }, [asmIdEstado, cargarEstado]);
  useEffect(() => {
    if (!asmIdEstado || !pestanaVisible) return;
    const t = setInterval(() => cargarEstado(asmIdEstado), ESTADO_MS);
    return () => clearInterval(t);
  }, [asmIdEstado, pestanaVisible, cargarEstado]);

  // ── Presencia en la asamblea ─────────────────────────────────────────────
  // F-26: el latido ya no vive en esta pantalla sino en PresenciaContext (a
  // nivel de toda la app): navegar a "Mis unidades" o "Mi perfil", ocultar la
  // pestaña o tener abierta la reunión de Zoom NO saca a la persona del
  // cuórum. Aquí solo se le avisa cuándo empieza (asistencia confirmada) y
  // cuándo ya no aplica (sin asamblea o sin confirmar).
  useEffect(() => {
    if (asmIdEstado && confirmed) presencia.iniciar(asmIdEstado);
  }, [asmIdEstado, confirmed, presencia]);
  useEffect(() => {
    if (!assemblyLoading && !asmIdEstado) presencia.detener();
    else if (asmIdEstado && estadoDe === asmIdEstado && !confirmed) presencia.detener(asmIdEstado);
  }, [assemblyLoading, asmIdEstado, estadoDe, confirmed, presencia]);

  // ── Cargar preguntas y votos cuando está confirmado ──────────────────────
  const loadResults = useCallback(async (assemblyId) => {
    try {
      const resR = await voteService.results(assemblyId);
      setData(resR.data);
    } catch {}
  }, []);
  const loadMine = useCallback(async (assemblyId) => {
    try {
      const resM = await voteService.myVotes(assemblyId);
      // F-21: una pregunta "incompleta" (el servidor no pudo guardar el voto de
      // todas sus unidades) sigue pendiente — lo dice el servidor, así que se
      // mantiene aunque recargue — y se completa con la respuesta ya dada.
      const map = {}, inc = {};
      (resM.data || []).forEach(v => {
        if (v.incompleta) inc[v.question_id] = { answer: v.answer, votadas: v.unidades_votadas, total: v.unidades_total };
        else map[v.question_id] = v.answer;
      });
      setMyVotes(map);
      setIncompletas(inc);
    } catch {}
  }, []);
  const loadVotes = useCallback(
    (assemblyId) => Promise.all([loadResults(assemblyId), loadMine(assemblyId)]),
    [loadResults, loadMine],
  );

  // Se depende de assembly?.id (no del objeto): checkAssembly lo renueva cada
  // 15 s y antes eso reiniciaba este ciclo y recargaba todo.
  const asmId = assembly?.id;
  useEffect(() => {
    if (!asmId || !confirmed) { setData(null); setMyVotes({}); return; }
    if (!pestanaVisible) return;
    loadVotes(asmId);
    voteIntervalRef.current = setInterval(() => loadResults(asmId), POLL_MS);
    const misVotosInt = setInterval(() => loadMine(asmId), MIS_VOTOS_MS);
    return () => { clearInterval(voteIntervalRef.current); clearInterval(misVotosInt); };
  }, [asmId, confirmed, pestanaVisible, loadVotes, loadResults, loadMine]);

  // Cuando cambian las preguntas activas, "mis votos" se refresca enseguida
  // (no espera el respaldo de 30 s).
  const activasKey = data ? data.questions.filter(q => q.active).map(q => q.id).join(',') : '';
  useEffect(() => {
    if (asmId && confirmed && activasKey) loadMine(asmId);
  }, [activasKey, asmId, confirmed, loadMine]);

  // ── Detectar nueva pregunta activa → abrir modal ─────────────────────────
  useEffect(() => {
    if (!data) return;
    const pending = data.questions.filter(q => q.active && !myVotes[q.id]).sort((a, b) => b.id - a.id);
    const newest  = pending[0] || null;
    setActiveQuestion(prev => {
      if (!newest) return null;
      if (prev?.id === newest.id) return prev;
      return newest;
    });
  }, [data, myVotes]);

  // ── Confirmar asistencia ─────────────────────────────────────────────────
  const confirmAttendance = async (consent) => {
    setConfirming(true);
    try {
      await attendanceService.confirm(assembly.id, consent);
      setConfirmed(true);
      setConsentGiven(true);
    } catch (err) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Error al confirmar asistencia' });
      cerrarAlertaEn(4000);
    } finally {
      setConfirming(false);
    }
  };

  // ── Salir de la asamblea ─────────────────────────────────────────────────
  // setConfirmed(false) dispara la limpieza del efecto de presencia, que manda
  // el "leave" (marca exit_time). Se llama también aquí de una vez por si el
  // efecto no estaba activo.
  const leaveAssembly = async () => {
    salioAManoRef.current = true;
    await presencia.salir();
    setConfirmed(false);
  };

  // ── Votar ────────────────────────────────────────────────────────────────
  // Tras un voto sin respuesta clara (conexión cortada, nginx sin backend,
  // página de error de Cloudflare), pregunta a "mis votos" —sin caché— si el
  // voto quedó. Así el votante nunca se queda con la duda de "¿votó o no?".
  const verificarVoto = async (questionId) => {
    try {
      const resM = await voteService.myVotes(assembly.id);
      const v = (resM.data || []).find(x => Number(x.question_id) === Number(questionId));
      if (!v) return 'no';
      return v.incompleta ? 'incompleto' : 'completo';
    } catch {
      return 'sin-conexion';
    }
  };

  const vote = async (questionId, value) => {
    setVoting(questionId);
    try {
      const r = await voteService.vote(questionId, value);
      setMyVotes(m => ({ ...m, [questionId]: value }));
      setActiveQuestion(null);
      // F-06: alguna unidad que representa ya había votado esta pregunta con su
      // propietario — antes se omitía en silencio y el aviso decía "exitosamente".
      const omitidas = r.data?.omitidas || [];
      if (omitidas.length) {
        const total = (r.data?.count || 0) + omitidas.length;
        // P-A: también puede faltar una unidad porque le quitaron el poder mientras votaba.
        const sinPoder = omitidas.filter(o => /poder/.test(o.motivo || ''));
        // #430: la votación se cerró mientras se registraba el voto (el servidor lo rechaza y no lo guarda)
        const cerradas = omitidas.filter(o => /cerró/.test(o.motivo || ''));
        const yaVotadas = omitidas.filter(o => !/poder/.test(o.motivo || '') && !/cerró/.test(o.motivo || ''));
        const partes = [`Tu voto se registró por ${r.data?.count} de ${total} unidades.`];
        if (yaVotadas.length) partes.push(`${yaVotadas.map(o => o.unidad).join(', ')} ya había votado esta pregunta con su propietario; ese voto se mantiene.`);
        if (sinPoder.length) partes.push(`Ya no representas ${sinPoder.map(o => o.unidad).join(', ')}: se quitó el poder.`);
        if (cerradas.length) partes.push(`${cerradas.map(o => o.unidad).join(', ')} no se ${cerradas.length > 1 ? 'registraron' : 'registró'}: la votación se cerró antes.`);
        setAlert({ type: 'warning', message: partes.join(' ') });
        cerrarAlertaEn(9000);
      } else if (r.data?.respuesta_mantenida !== undefined) {
        setAlert({ type: 'warning', message: r.data.message });
        cerrarAlertaEn(9000);
      } else {
        setAlert({ type: 'success', message: '¡Voto registrado exitosamente!' });
        cerrarAlertaEn(4000);
      }
      loadVotes(assembly.id);
    } catch (err) {
      // F-16: el servidor exige haber pulsado "Participar" en esta asamblea;
      // si la sesión quedó desfasada, se vuelve a la pantalla de registro.
      if (err.response?.data?.code === 'ATTENDANCE_REQUIRED') { setActiveQuestion(null); setConfirmed(false); }
      // F-06: todas sus unidades ya las votaron sus propietarios — no hay nada que votar.
      if (err.response?.data?.code === 'UNITS_ALREADY_VOTED') { setActiveQuestion(null); loadVotes(assembly.id); }
      // P-A: le quitaron el poder mientras votaba — ya no tiene esas unidades.
      if (err.response?.data?.code === 'POWER_REVOKED') { setActiveQuestion(null); checkAssembly(); }
      // F-21: el voto (o parte) no se guardó — la pregunta queda pendiente y la
      // ventana de votación abierta; /votes/my dirá si quedó incompleta.
      const codigo = err.response?.data?.code;
      const noGuardado = codigo === 'VOTE_NOT_SAVED';
      if (noGuardado) {
        setMyVotes(m => { const n = { ...m }; delete n[questionId]; return n; });
        loadVotes(assembly.id);
      }
      // Sin respuesta clara: la conexión se cortó, o respondió nginx/Cloudflare
      // en vez del backend (504 = el backend pudo guardarlo igual). Se verifica.
      const status = err.response?.status;
      const ambiguo = !err.response
        || (status >= 500 && codigo !== 'VOTE_NOT_SAVED' && codigo !== 'SERVIDOR_OCUPADO');
      if (ambiguo) {
        setAlert({ type: 'info', message: 'Verificando tu voto…' });
        const estado = await verificarVoto(questionId);
        const avisos = {
          completo:       { type: 'success', message: 'Tu voto sí quedó registrado ✓ (la conexión se cortó antes de que llegara la confirmación).' },
          incompleto:     { type: 'warning', message: 'Tu voto quedó incompleto: se registró solo por algunas de tus unidades. Pulsa «Completar mi voto».' },
          no:             { type: 'danger',  message: 'No encontramos tu voto registrado por un problema de conexión. Vuelve a intentarlo; si ya había quedado te lo indicaremos, nunca se cuenta dos veces.' },
          'sin-conexion': { type: 'danger',  message: 'No hay conexión con el servidor. Revisa tu internet y vuelve a intentarlo; tu voto nunca se cuenta dos veces.' },
        };
        if (estado === 'completo') { setMyVotes(m => ({ ...m, [questionId]: value })); setActiveQuestion(null); }
        if (estado !== 'sin-conexion') loadVotes(assembly.id);
        setAlert(avisos[estado]);
        cerrarAlertaEn(estado === 'completo' ? 6000 : 9000);
        return;
      }
      // 422 (p. ej. "Ya has votado": un reintento de un voto que sí había
      // quedado): refrescar "mis votos" ya — se consulta cada 30 s y la ventana
      // quedaría abierta hasta entonces.
      if (status === 422) loadVotes(assembly.id);
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Error al votar' });
      cerrarAlertaEn(noGuardado ? 9000 : 4000);
    } finally {
      setVoting(null);
    }
  };

  const elegirAsamblea = (id) => {
    try { localStorage.setItem('eventify_asamblea_elegida', String(id)); } catch { /* sin almacenamiento: igual cambia */ }
    checkAssembly();
  };
  const selector = asambleas.length > 1 && (
    <div className={`card ${styles.selectorAsambleas}`}>
      <div className={styles.selectorTitulo}>
        <i className={`fas fa-layer-group ${styles.icono8} ${styles.colorAcento}`} />
        Tienes {asambleas.length} asambleas en curso
      </div>
      <div className={styles.selectorBotones}>
        {asambleas.map(a => (
          <button key={a.id} type="button" onClick={() => elegirAsamblea(a.id)}
            className={`${a.id === assembly?.id ? 'btn btn-primary' : 'btn btn-outline'} ${styles.selectorBoton}`}>
            {a.conjunto} · #{a.number_assembly}{a.rol === 'representante' ? ' (con poder)' : ''}
          </button>
        ))}
      </div>
      <div className={styles.selectorNota}>
        Tu presencia cuenta en la asamblea que tienes abierta. Para estar presente en las dos al mismo tiempo, abre cada una en un dispositivo distinto.
      </div>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────
  if (assemblyLoading) return <div className="loading"><div className="spinner" /></div>;

  // Propiedad bloqueada (+14 días sin renovar) → pantalla de bloqueo total
  if (propertyBlocked) return <PropertyBlockedScreen />;

  if (!assembly) return (
    <>
      {contactChecked && !user?.is_temporary_rep && <ContactBanner phone={contactPhone} />}
      <NoAssemblyScreen />
    </>
  );

  if (estadoDe !== assembly.id) return <div className="loading"><div className="spinner" /></div>;

  // Dio poder por TODAS sus unidades: su representante asiste y vota por él.
  if (!confirmed && delegacion.all) return (
    <>
      {contactChecked && !user?.is_temporary_rep && <ContactBanner phone={contactPhone} />}
      {selector}
      <DelegatedScreen assembly={assembly} delegated={delegacion.delegated} onJoinMeeting={unirseReunion} />
    </>
  );

  // Dio poder solo por ALGUNAS unidades: participa con las demás, con aviso.
  const avisoDelegacion = delegacion.delegated.length > 0 && (
    <div className={`alert ${styles.avisoDelegacion}`}>
      <i className={`fas fa-file-signature ${styles.colorMorado} ${styles.icono8}`} />
      Diste poder a {delegacion.delegated.map(d => `${d.rep_nombre} (unidad${d.unidades.length > 1 ? 'es' : ''} ${d.unidades.join(', ')})`).join(' y ')}:
      esa persona vota por esas unidades. Tú participas con las demás.
    </div>
  );

  if (!confirmed) {
    return (
      <>
        {contactChecked && !user?.is_temporary_rep && <ContactBanner phone={contactPhone} />}
        {selector}
        {avisoDelegacion}
        {alert && (
          <div className={`alert alert-${alert.type} ${styles.salaAviso}`} onClick={() => setAlert(null)}>
            {alert.message}
          </div>
        )}
        <ConfirmAttendanceScreen assembly={assembly} onConfirm={confirmAttendance} confirming={confirming} propertyReadOnly={propertyReadOnly} consentGiven={consentGiven} />
      </>
    );
  }

  return (
    <>
      {selector}
      {avisoDelegacion}
      <VotingScreen
        assembly={assembly}
        data={data}
        myVotes={myVotes}
        incompletas={incompletas}
        activeQuestion={activeQuestion}
        setActiveQuestion={setActiveQuestion}
        vote={vote}
        voting={voting}
        alert={alert}
        setAlert={setAlert}
        user={user}
        propertyReadOnly={propertyReadOnly}
        onJoinMeeting={unirseReunion}
        onLeave={leaveAssembly}
        attendanceMode={attendanceMode}
        onModeSaved={setAttendanceMode}
        voto={voto}
      />
    </>
  );
}